// Console logs are for the user for local event logging
/* eslint-disable no-console */

import { getConfig } from '@leagueplatform/config';
import { ExportAnalyticsEvent, localLogStore } from './local-log-store';
import {
  captureViewportScreenshot,
  isScreenshotSupported,
  openScreenshot,
} from './screenshot-utils';
import { ConsoleKey, LocalStorageEnableKey } from './constants';

// Constants used to manage log limits and notifications
export const LogEventLimit = 50;
const LogLengthNotifierUnit = 10;

// Cache for the configuration to avoid repeated calls
let cachedConfig: ReturnType<typeof getConfig> | null = null;

const getMemoizedConfig = () => {
  if (!cachedConfig) {
    cachedConfig = getConfig(false);
  }
  return cachedConfig;
};

// Reset cache function for testing
export const resetConfigCache = () => {
  cachedConfig = null;
};

/* Check if debug logging is enabled via 3 layers:
 * 1) App config specifies a non-production environment
 * 2) App config specifies that local analytics logging should be enabled
 * 3) Local storage specifies that local analytics logging should be enabled
 *    Which means a user only sees the local log events if they specifically
 *    enable it on their device.
 */
const getIsDebugLoggingEnabled = () => {
  const { config } = getMemoizedConfig();
  return (
    Boolean(config?.core?.appEnvironment) &&
    config?.core?.appEnvironment !== 'production' &&
    Boolean(config?.core?.analytics?.shouldEnableLocalAnalyticsLogging) &&
    localStorage.getItem(LocalStorageEnableKey) === 'true'
  );
};

// Method to reset the local user events log
// On user logout, the auth module directly clears
// localStorage.removeItem('local-user-event-log') to avoid
// creating a dependency between the auth and analytics modules
export const resetUserEventsLog = () => {
  localLogStore.getState().clearAndResetLog();
  return `${ConsoleKey} Info: User events log has been reset. New events can now be added.`;
};

// Set up the window to expect global functions to manage the user events log
declare global {
  interface Window {
    resetUserEventsLog: () => void;
    openScreenshot: (index: number) => void;
  }
}

// Make the functions available to the window so they can be called in the console
window.resetUserEventsLog = resetUserEventsLog;
window.openScreenshot = openScreenshot;

// Function to add events to the local debug logger and inform user of log status
export const handleDebugLogging = async (event: ExportAnalyticsEvent) => {
  // Add events to the debug logger if enabled
  if (getIsDebugLoggingEnabled()) {
    const { localLog } = localLogStore.getState();
    // Check if the log is full
    if (localLog.length === LogEventLimit) {
      console.log(
        `${ConsoleKey} Warning: Local log is full and new events cannot be added.\nType \`resetUserEventsLog()\` and hit \`enter\` in the console to reset the log and continue logging new events.`,
      );
    } else {
      // Get current URL path with parameters
      const currentPath = window.location.pathname + window.location.search;
      const newMetadata = {
        eventUrl: currentPath,
      };

      // Merge existing metadata with new metadata
      const existingMetadata = event.props?.metadata || {};
      const mergedMetadata = {
        ...existingMetadata,
        ...newMetadata,
      };

      // Capture screenshot if supported
      let eventToStore = {
        ...event,
        props: {
          ...event.props,
          metadata: mergedMetadata,
        },
      };
      if (isScreenshotSupported()) {
        try {
          const screenshotData = await captureViewportScreenshot();
          if (screenshotData) {
            // Store screenshot separately
            const { screenshots } = localLogStore.getState();
            localLogStore.getState().pushScreenshot(screenshotData);
            // Link event to screenshot using array index
            eventToStore = {
              ...eventToStore,
              screenshotIndex: screenshots.length,
            };
          }
        } catch (error) {
          console.warn(
            `${ConsoleKey} Warning: Screenshot capture failed:`,
            error,
          );
        }
      }

      // Store the event
      localLogStore.getState().pushEvent(eventToStore);

      const newLength = localLog.length + 1;

      console.log(`${ConsoleKey} Logging:`, eventToStore);

      // Notify the user of log length periodically
      if (
        newLength % LogLengthNotifierUnit === 0 &&
        newLength !== LogEventLimit
      ) {
        console.log(`${ConsoleKey} Info: Log length is now ${newLength}.`);
      }

      // Warn the user if the log is about to be full
      if (newLength === LogEventLimit - 1) {
        console.log(
          `${ConsoleKey} Warning: only 1 more event can be added before data must be exported.`,
        );
      }

      // Instruct the user if the log is now full
      if (newLength === LogEventLimit) {
        console.log(
          `${ConsoleKey} Warning: Local log is now full. Type \`resetUserEventsLog()\` in the console to reset the log.`,
        );
      }
    }
  }
};
